import { A, pipe } from "@mobily/ts-belt"
import { extractFromProseMirrorDoc } from "@shared/lib/card-stack/extracted"
import { initialIdList } from "@shared/lib/card-stack/prose-mirror-utils"
import { cardStackDocFromLegacyCards } from "@shared/lib/card-stack/unified-migration"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { UnitCreate } from "@shared/models/analytics/unit"
import type { LessonCard } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import { createCourseDateCustomAttributes } from "@shared/models/course"
import Patch from "@shared/models/patch-updater"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { consolidateCourseDatesOff, constructUnitsAndDates } from "cc-frontend/lib/unit-utils"
import { isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { cloneDeep, get, map } from "lodash-es"
import moment from "moment"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  newUnitId: string
  unitStart: string
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  unitCards: Array<LessonCard>
}

export default {
  name: "ADD_UNIT",

  params: {
    newUnitId: "string",
    unitStart: "string",
    course: "object",
    planbook: "object",
    rotationCalendar: "object",
    unitCards: "object",
  },

  patches(payload: Payload) {
    const isUnified = payload.planbook.attributes.flags.unifiedLessons
    let createUnitPatch = Patch.create("card-stack", payload.newUnitId)
      .set("attributes.schemaVersion", isUnified ? 2 : 1)
      .set("attributes.usedAs", "unit")
      .set("attributes.cards", payload.unitCards)
      .set("meta", { permissions: ["can-edit-plan", "can-view-plan"] })
      .set("relationships.course.data", {
        id: payload.course.id,
        type: "course",
      })
      .set("relationships.planbook.data", {
        id: payload.planbook.id,
        type: "planbook",
      })
    if (isUnified) {
      const doc = cardStackDocFromLegacyCards(payload.unitCards)
      createUnitPatch
        .set("attributes.proseMirror", {
          json: doc.toJSON(),
          ids: initialIdList(doc).save(),
        })
        .set("attributes.extracted", extractFromProseMirrorDoc(doc))
    }

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let courseDateExists = pipe(
      payload.course.attributes.calendar.dates,
      A.filter(isCourseDateCustom),
      A.find((date) => date.attributes.dateString === payload.unitStart)
    )
    let courseDatesWithUnit
    if (courseDateExists === undefined || courseDateExists === null) {
      courseDatesWithUnit = payload.course.attributes.calendar.dates.concat([
        {
          id: payload.unitStart,
          type: "course-date-custom",
          attributes: createCourseDateCustomAttributes({
            dateString: payload.unitStart,
            unitStart: [payload.newUnitId],
            unitEnd: [payload.newUnitId],
            unitIds: [payload.newUnitId],
          }),
        },
      ])
    } else {
      courseDatesWithUnit = map(payload.course.attributes.calendar.dates, (courseDate) => {
        if (
          isCourseDateCustom(courseDate) &&
          courseDate.attributes.dateString === payload.unitStart
        ) {
          let cd = cloneDeep(courseDate)
          cd.attributes.unitStart.push(payload.newUnitId)
          cd.attributes.unitEnd.push(payload.newUnitId)
          return cd
        } else {
          return courseDate
        }
      })
    }

    let { courseDates, units } = constructUnitsAndDates(
      courseDatesWithUnit,
      consolidatedCourseDatesOff,
      payload.rotationCalendar.attributes.schoolDays
    )

    let addUnitToCalendarPatch = Patch.create("course", payload.course.id)
      .set("attributes.calendar.dates", courseDates)
      .set("attributes.calendar.units", units)
      .inc("attributes.calendar.version", 1).patch

    return [createUnitPatch.patch, addUnitToCalendarPatch]
  },

  undoPatches(payload: Payload) {
    return Patch.create("course", payload.course.id)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      )
      .inc("attributes.calendar.version", 1).wrapInArray
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let course = payload.course
    let models = {
      course: course,
      planbook: findFn("planbook", get(course, "relationships.planbook.data.id") as string),
      unit:
        findFn("card-stack", payload.newUnitId) || findFn("card-stack-summary", payload.newUnitId),
    }
    let courseTitle = get(models, "course.attributes.title")

    let newDateFormatted = moment(payload.unitStart).format("ddd, MMM. D, YYYY")
    let newEvent: UnitCreate = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: null,
      primary_verb: "create",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),

      primary_object_type: "unit",
      primary_object_id: payload.newUnitId,
      primary_object_specifier: null,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

      adverbial_verb: "adding",

      adverbial_object_type: "unit",
      adverbial_object_id: payload.newUnitId,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    }

    return {
      title: "Added Unit",
      titleForAnalytics: "Unit - Added",
      description: `Added unit to your ${courseTitle} class on ${newDateFormatted}`,
      context: {
        courseTitle,
        planbookTitle: get(models, "planbook.attributes.title"),
        unitId: payload.newUnitId,
        courseId: get(models, "course.id"),
        planbookId: get(models, "planbook.id"),
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `course-${payload.course.id}`,
        to: [
          `course-edits:course-${payload.course.id}`,
          `planbook-edits:planbook-${models.planbook.id}`,
        ],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}
